import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { fetchProductEventAnalytics } from "../../services";
import ReactECharts from "echarts-for-react";
import * as XLSX from "xlsx";
import {
  Eye,
  Users,
  MessageSquare,
  Search,
  RefreshCw,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Download,
  Calendar,
  AlertCircle,
  Smartphone,
  Monitor,
  Activity,
  Layers,
  Sparkles,
  Tag,
  SlidersHorizontal,
} from "lucide-react";

// ── Helpers ──
const formatINR = (val) => {
  const num = Number(val) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
};

const formatCompactINR = (val) => {
  const num = Number(val) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)} K`;
  return `₹${num.toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(dateStr);
};

const QUICK_PRESETS = [
  { label: "All Time", days: null },
  { label: "Today", days: 0 },
  { label: "Last 7 Days", days: 7 },
  { label: "Last 30 Days", days: 30 },
  { label: "Last 90 Days", days: 90 },
];

const ProductEventCount = () => {
  // ── States ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Active Tab
  const [activeTab, setActiveTab] = useState("products"); // "products" | "contacts" | "traffic"

  // Date Filters
  const [selectedPreset, setSelectedPreset] = useState("All Time");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Product Directory Filters & Pagination
  const [productSearch, setProductSearch] = useState("");
  const [productSort, setProductSort] = useState("-views");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Contact Filter
  const [contactFilterType, setContactFilterType] = useState("all");
  const [contactSearch, setContactSearch] = useState("");

  // Search debounce ref
  const searchTimeoutRef = useRef(null);

  // ── Fetch Analytics ──
  const loadAnalytics = useCallback(
    async (overrideParams = {}) => {
      setLoading(true);
      setError("");
      try {
        const params = {
          from_date: overrideParams.from_date !== undefined ? overrideParams.from_date : fromDate,
          to_date: overrideParams.to_date !== undefined ? overrideParams.to_date : toDate,
          search: overrideParams.search !== undefined ? overrideParams.search : productSearch,
          sort_by: overrideParams.sort_by !== undefined ? overrideParams.sort_by : productSort,
          page: overrideParams.page !== undefined ? overrideParams.page : page,
          per_page: overrideParams.per_page !== undefined ? overrideParams.per_page : perPage,
        };

        const res = await fetchProductEventAnalytics(params);
        if (res) {
          setData(res);
        } else {
          setError("No data received from analytics server");
        }
      } catch (err) {
        console.error("Failed to load product analytics:", err);
        setError("Failed to load product event analytics. Please check server or authentication.");
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, productSearch, productSort, page, perPage]
  );

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  // Handle Preset Change
  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.label);
    if (preset.days === null) {
      setFromDate("");
      setToDate("");
      setPage(1);
      loadAnalytics({ from_date: "", to_date: "", page: 1 });
      return;
    }

    const today = new Date();
    const toStr = today.toISOString().slice(0, 10);
    const fromD = new Date();
    fromD.setDate(fromD.getDate() - preset.days);
    const fromStr = fromD.toISOString().slice(0, 10);

    setFromDate(fromStr);
    setToDate(toStr);
    setPage(1);
    loadAnalytics({ from_date: fromStr, to_date: toStr, page: 1 });
  };

  // Handle Search Input Debounce
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setProductSearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      loadAnalytics({ search: val, page: 1 });
    }, 400);
  };

  // Handle Sorting Change
  const handleSortChange = (newSort) => {
    setProductSort(newSort);
    setPage(1);
    loadAnalytics({ sort_by: newSort, page: 1 });
  };

  // Handle Page Change
  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadAnalytics({ page: newPage });
  };

  // Export Data to Excel
  const handleExportExcel = () => {
    if (!data) return;

    // Sheet 1: Visited Products
    const productsSheetData = (data.products_visited?.results || []).map((p, idx) => ({
      "#": idx + 1,
      "Product Name": p.equip_name,
      Brand: p.brand,
      Model: p.model,
      "Asking Price (INR)": p.asking_price,
      "Total Views": p.views,
      "Total Form Submissions": p.total_form_submissions || 0,
      "CardPopup Submissions": p.card_popup_submissions || 0,
      "ContactForPrice Submissions": p.contact_for_price_submissions || 0,
      "Product URL": p.slug ? `https://www.equipmedy.com/${p.slug}` : "—",
      "Last Visited": formatDate(p.last_visit),
    }));

    // Sheet 2: Contact Form Submissions
    const contactsSheetData = (data.contact_submissions?.recent_submissions || []).map((c, idx) => ({
      "#": idx + 1,
      Name: c.name,
      Email: c.email,
      "Contact Number": c.contact_no,
      "Form Source": c.form_type,
      "Product Inquired": c.product_name,
      "Product URL": c.product_url,
      "Message / Details": c.message,
      "Submission Date": formatDate(c.created_date),
    }));

    // Sheet 3: Traffic Summary
    const trafficSheetData = [
      { Metric: "Total Products Visited", Value: data.summary?.total_products_visited || 0 },
      { Metric: "Total Product Views", Value: data.summary?.total_product_views || 0 },
      { Metric: "Total Products Value (INR)", Value: data.summary?.total_products_price || 0 },
      { Metric: "Average Product Price (INR)", Value: data.summary?.average_product_price || 0 },
      { Metric: "Total Users / Visitors", Value: data.traffic?.total_users || 0 },
      { Metric: "Logged In Users", Value: data.traffic?.logged_in_users || 0 },
      { Metric: "Guest Users", Value: data.traffic?.guest_users || 0 },
      { Metric: "Unique IP Addresses", Value: data.traffic?.unique_ips || 0 },
      { Metric: "Total Contact Submissions", Value: data.contact_submissions?.total_submissions || 0 },
      { Metric: "CardPopup Submissions", Value: data.contact_submissions?.card_popup_submissions || 0 },
      { Metric: "ContactForPrice Submissions", Value: data.contact_submissions?.contact_for_price_submissions || 0 },
    ];

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(productsSheetData);
    const ws2 = XLSX.utils.json_to_sheet(contactsSheetData);
    const ws3 = XLSX.utils.json_to_sheet(trafficSheetData);

    XLSX.utils.book_append_sheet(wb, ws1, "Visited Products");
    XLSX.utils.book_append_sheet(wb, ws2, "Contact Submissions");
    XLSX.utils.book_append_sheet(wb, ws3, "Summary & Traffic");

    XLSX.writeFile(wb, `Product_Event_Analytics_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Derived Data for Visual Charts ──
  const formBreakdownOption = useMemo(() => {
    if (!data?.contact_submissions) return null;
    const cs = data.contact_submissions;
    const cardPopup = cs.card_popup_submissions || 0;
    const cfp = cs.contact_for_price_submissions || 0;
    const general = cs.general_submissions || 0;

    return {
      tooltip: {
        trigger: "item",
        formatter: "{b}: <b>{c}</b> ({d}%)",
      },
      legend: {
        bottom: "0%",
        left: "center",
        icon: "circle",
        itemGap: 14,
        textStyle: { color: "#475569", fontSize: 12 },
      },
      color: ["#0284c7", "#10b981", "#64748b"],
      series: [
        {
          name: "Form Submissions",
          type: "pie",
          radius: ["48%", "72%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 6,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: false,
            position: "center",
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: "bold",
              formatter: "{b}\n{c}",
            },
          },
          data: [
            { value: cardPopup, name: "CardPopup Inquiries" },
            { value: cfp, name: "ContactForPrice (Ask For Price)" },
            { value: general, name: "General Inquiries" },
          ],
        },
      ],
    };
  }, [data]);

  const topProductsChartOption = useMemo(() => {
    if (!data?.products_visited?.top_products?.length) return null;
    const top = [...data.products_visited.top_products].reverse();
    const names = top.map((t) => (t.name.length > 22 ? t.name.slice(0, 20) + "…" : t.name));
    const views = top.map((t) => t.views);
    const prices = top.map((t) => t.price);

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const idx = params[0].dataIndex;
          const fullItem = top[idx];
          return `
            <div style="font-weight:600; margin-bottom:4px;">${fullItem.name}</div>
            <div style="color:#0284c7;">Views: <b>${fullItem.views.toLocaleString("en-IN")}</b></div>
            <div style="color:#059669;">Price: <b>${formatINR(fullItem.price)}</b></div>
          `;
        },
      },
      grid: {
        left: "4%",
        right: "6%",
        bottom: "6%",
        top: "6%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        name: "Views",
        splitLine: { lineStyle: { type: "dashed", color: "#e2e8f0" } },
      },
      yAxis: {
        type: "category",
        data: names,
        axisLine: { lineStyle: { color: "#cbd5e1" } },
        axisLabel: { color: "#334155", fontSize: 11 },
      },
      series: [
        {
          name: "Views",
          type: "bar",
          data: views,
          barMaxWidth: 20,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: "#38bdf8" },
                { offset: 1, color: "#0284c7" },
              ],
            },
            borderRadius: [0, 4, 4, 0],
          },
        },
      ],
    };
  }, [data]);

  // Filtered Contact Submissions
  const filteredContacts = useMemo(() => {
    if (!data?.contact_submissions?.recent_submissions) return [];
    let list = data.contact_submissions.recent_submissions;

    if (contactFilterType !== "all") {
      list = list.filter((c) => c.form_type === contactFilterType);
    }

    if (contactSearch.trim()) {
      const q = contactSearch.toLowerCase();
      list = list.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.contact_no?.includes(q) ||
          c.product_name?.toLowerCase().includes(q)
      );
    }

    return list;
  }, [data, contactFilterType, contactSearch]);

  const summary = data?.summary || {};
  const traffic = data?.traffic || {};
  const contactStats = data?.contact_submissions || {};
  const pagination = data?.products_visited?.pagination || { page: 1, total_pages: 1, total: 0 };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* ── Top Header Bar ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
                Product Event & Traffic Analytics
              </h1>
              <p className="text-xs sm:text-sm text-slate-500">
                Comprehensive tracking of visited products, pricing inventory, visitor traffic, and popup form submissions
              </p>
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Quick Date Presets */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
            {QUICK_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => handlePresetSelect(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  selectedPreset === p.label
                    ? "bg-white text-blue-700 shadow-sm font-semibold"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Export to Excel */}
          <button
            onClick={handleExportExcel}
            disabled={loading || !data}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all disabled:opacity-50"
            title="Download full analytics as Excel"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export</span>
          </button>

          {/* Refresh Button */}
          <button
            onClick={() => loadAnalytics()}
            disabled={loading}
            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 transition-all disabled:opacity-50"
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-blue-600" : ""}`} />
          </button>
        </div>
      </div>

      {/* ── Custom Date Pickers Sub-bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white px-4 py-3 rounded-xl border border-slate-200 shadow-sm text-xs">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-medium text-slate-600 flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-blue-600" /> Date Filter:
          </span>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setSelectedPreset("Custom");
              }}
              className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setSelectedPreset("Custom");
              }}
              className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <button
            onClick={() => {
              setPage(1);
              loadAnalytics({ from_date: fromDate, to_date: toDate, page: 1 });
            }}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all"
          >
            Apply Dates
          </button>
          {(fromDate || toDate) && (
            <button
              onClick={() => {
                setFromDate("");
                setToDate("");
                setSelectedPreset("All Time");
                setPage(1);
                loadAnalytics({ from_date: "", to_date: "", page: 1 });
              }}
              className="text-xs text-rose-600 hover:underline font-medium"
            >
              Clear Filter
            </button>
          )}
        </div>

        {/* Status indicator */}
        <div className="text-slate-500 text-xs flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          <span>Live Tracking Active</span>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div className="flex items-center gap-2.5 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-sm">
          <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* ── KPI Stat Cards Grid (Core Requirements 1, 2, 3) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 1: Visited Products Count */}
        <div className="relative bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all overflow-hidden group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Products Visited
            </span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
              <Eye className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 tracking-tight">
            {loading ? "…" : (summary.total_products_visited || 0).toLocaleString("en-IN")}
          </div>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <span className="font-semibold text-blue-600">
              {(summary.total_product_views || 0).toLocaleString("en-IN")}
            </span>{" "}
            total views
          </p>
        </div>

        {/* Card 2: Visited Products Total Value */}
        <div className="relative bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all overflow-hidden group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Inventory Value
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 font-bold">
              ₹
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 tracking-tight">
            {loading ? "…" : formatCompactINR(summary.total_products_price || 0)}
          </div>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            Avg: <span className="font-semibold text-emerald-600">{formatINR(summary.average_product_price || 0)}</span>
          </p>
        </div>

        {/* Card 3: Traffic & Unique Users Count */}
        <div className="relative bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all overflow-hidden group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Total Traffic
            </span>
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 tracking-tight">
            {loading ? "…" : (traffic.total_users || 0).toLocaleString("en-IN")}
          </div>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <span className="font-semibold text-indigo-600">{traffic.logged_in_users || 0}</span> users •{" "}
            <span className="font-semibold text-purple-600">{traffic.guest_users || 0}</span> guests
          </p>
        </div>

        {/* Card 4: Total Form Submissions */}
        <div className="relative bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all overflow-hidden group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-pink-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Form Inquiries
            </span>
            <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600">
              <MessageSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 tracking-tight">
            {loading ? "…" : (contactStats.total_submissions || 0).toLocaleString("en-IN")}
          </div>
          <p className="text-xs text-slate-500 mt-1">Total inquiries received</p>
        </div>

        {/* Card 5: CardPopup Form Submissions */}
        <div className="relative bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all overflow-hidden group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-sky-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              CardPopup Leads
            </span>
            <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center text-cyan-600">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-cyan-700 tracking-tight">
            {loading ? "…" : (contactStats.card_popup_submissions || 0).toLocaleString("en-IN")}
          </div>
          <p className="text-xs text-slate-500 mt-1">Normal Popup Inquiries</p>
        </div>

        {/* Card 6: ContactForPrice Submissions */}
        <div className="relative bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all overflow-hidden group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Price Quotes
            </span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
              <Tag className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-amber-700 tracking-tight">
            {loading ? "…" : (contactStats.contact_for_price_submissions || 0).toLocaleString("en-IN")}
          </div>
          <p className="text-xs text-slate-500 mt-1">ContactForPrice Submissions</p>
        </div>
      </div>

      {/* ── Visual Analytics Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart 1: Top Visited Products (Views & Price) */}
        <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-bold text-slate-900">Top Visited Products by Views</h2>
              <p className="text-xs text-slate-500">
                Most visited equipment items on EquipMedy platform
              </p>
            </div>
            <span className="text-xs px-2.5 py-1 bg-blue-50 text-blue-700 font-semibold rounded-full border border-blue-200">
              Top 10 Visited
            </span>
          </div>

          <div className="h-72 w-full">
            {loading ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Loading charts…
              </div>
            ) : topProductsChartOption ? (
              <ReactECharts
                option={topProductsChartOption}
                style={{ height: "100%", width: "100%" }}
                opts={{ renderer: "canvas" }}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                No product visit data available
              </div>
            )}
          </div>
        </div>

        {/* Chart 2: Contact Form Submissions Breakdown */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-bold text-slate-900">Contact Forms Breakdown</h2>
                <p className="text-xs text-slate-500">
                  CardPopup vs ContactForPrice submission distribution
                </p>
              </div>
            </div>

            <div className="h-56 w-full">
              {loading ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  Loading charts…
                </div>
              ) : formBreakdownOption ? (
                <ReactECharts
                  option={formBreakdownOption}
                  style={{ height: "100%", width: "100%" }}
                  opts={{ renderer: "canvas" }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  No submissions data available
                </div>
              )}
            </div>
          </div>

          {/* Quick Metrics Bar below chart */}
          <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
            <div className="bg-cyan-50/70 p-2.5 rounded-xl border border-cyan-100">
              <span className="text-slate-500 font-medium block">CardPopup Inquiries</span>
              <span className="text-sm font-bold text-cyan-800">
                {contactStats.card_popup_submissions || 0}
              </span>
            </div>
            <div className="bg-emerald-50/70 p-2.5 rounded-xl border border-emerald-100">
              <span className="text-slate-500 font-medium block">Ask For Price Quotes</span>
              <span className="text-sm font-bold text-emerald-800">
                {contactStats.contact_for_price_submissions || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Data Explorer with Tabs ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Navigation Tabs Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-6 pt-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab("products")}
              className={`pb-3 px-3 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${
                activeTab === "products"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Eye className="w-4 h-4" />
              <span>Visited Products & Pricing</span>
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-bold">
                {summary.total_products_visited || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("contacts")}
              className={`pb-3 px-3 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${
                activeTab === "contacts"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Contact Form Submissions</span>
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 font-bold">
                {contactStats.total_submissions || 0}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("traffic")}
              className={`pb-3 px-3 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${
                activeTab === "traffic"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Traffic & User Breakdown</span>
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-bold">
                {traffic.total_users || 0}
              </span>
            </button>
          </div>
        </div>

        {/* ── TAB 1: Visited Products & Pricing Directory ── */}
        {activeTab === "products" && (
          <div className="p-5 space-y-4">
            {/* Search and Sorting Controls */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search products by title, brand, or model…"
                  value={productSearch}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                  <ArrowUpDown className="w-3.5 h-3.5" /> Sort:
                </span>
                <select
                  value={productSort}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="-submissions">Most Form Submissions First</option>
                  <option value="submissions">Least Form Submissions First</option>
                  <option value="-views">Most Visited First</option>
                  <option value="views">Least Visited First</option>
                  <option value="-price">Highest Price First</option>
                  <option value="price">Lowest Price First</option>
                  <option value="name">Product Name (A-Z)</option>
                  <option value="-name">Product Name (Z-A)</option>
                  <option value="-date">Recently Updated</option>
                </select>

                <select
                  value={perPage}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setPerPage(newSize);
                    setPage(1);
                    loadAnalytics({ per_page: newSize, page: 1 });
                  }}
                  className="px-2.5 py-1.5 border border-slate-300 rounded-xl text-xs text-slate-700 bg-white focus:outline-none"
                >
                  <option value="10">10 / page</option>
                  <option value="25">25 / page</option>
                  <option value="50">50 / page</option>
                </select>
              </div>
            </div>

            {/* Products Table */}
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="py-3 px-4">#</th>
                    <th className="py-3 px-4">Product Details</th>
                    <th className="py-3 px-4">Brand / Model</th>
                    <th className="py-3 px-4">Asking Price</th>
                    <th className="py-3 px-4">Total Visits</th>
                    <th className="py-3 px-4">Last Visit</th>
                    <th className="py-3 px-4 text-center">Total Form Submissions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="py-12 text-center text-slate-400">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
                        Loading visited products…
                      </td>
                    </tr>
                  ) : !data?.products_visited?.results?.length ? (
                    <tr>
                      <td colSpan="7" className="py-12 text-center text-slate-400">
                        No visited products found matching your filters.
                      </td>
                    </tr>
                  ) : (
                    data.products_visited.results.map((prod, idx) => (
                      <tr key={prod.id || prod.uid} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-mono text-xs text-slate-400">
                          {(page - 1) * perPage + idx + 1}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-lg border border-slate-200 bg-slate-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                              {prod.image ? (
                                <img
                                  src={prod.image}
                                  alt={prod.equip_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <Eye className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                            <div className="min-w-0">
                              {prod.slug ? (
                                <a
                                  href={`https://www.equipmedy.com/${prod.slug}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-semibold text-slate-900 hover:text-blue-600 transition-colors truncate max-w-md inline-flex items-center gap-1 group"
                                  title="Open product on EquipMedy"
                                >
                                  <span className="truncate">{prod.equip_name}</span>
                                  <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-blue-600 flex-shrink-0" />
                                </a>
                              ) : (
                                <p className="font-semibold text-slate-900 truncate max-w-md">
                                  {prod.equip_name}
                                </p>
                              )}
                              <span className="text-[11px] text-slate-400 font-mono block">
                                UID: {prod.uid?.slice(0, 8)}…
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-xs">
                            <span className="font-medium text-slate-800">{prod.brand || "—"}</span>
                            {prod.model && prod.model !== "—" && (
                              <span className="text-slate-400 block text-[11px]">{prod.model}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg font-bold text-xs">
                            {formatINR(prod.asking_price)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-blue-700 text-sm">
                              {prod.views.toLocaleString("en-IN")}
                            </span>
                            <span className="text-[10px] uppercase font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                              views
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">
                          {formatRelativeTime(prod.last_visit)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center justify-center gap-1.5">
                            {/* Total Count Badge */}
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold text-xs shadow-sm ${
                                prod.total_form_submissions > 0
                                  ? "bg-gradient-to-r from-purple-100 to-indigo-100 text-indigo-900 border border-indigo-200"
                                  : "bg-slate-100 text-slate-500 border border-slate-200"
                              }`}
                            >
                              <MessageSquare className="w-3.5 h-3.5 text-indigo-600" />
                              <span className="text-sm font-extrabold">{prod.total_form_submissions || 0}</span>
                              <span className="font-medium text-[11px] text-slate-600">
                                {prod.total_form_submissions === 1 ? "Inquiry" : "Inquiries"}
                              </span>
                            </span>

                            {/* Breakdown of both contact forms */}
                            <div className="flex items-center gap-1.5 text-[11px]">
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-50 text-cyan-800 border border-cyan-200 rounded-md font-medium"
                                title="CardPopup inquiries"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
                                <span className="text-slate-500">Popup:</span>
                                <span className="font-bold text-cyan-900">{prod.card_popup_submissions || 0}</span>
                              </span>

                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-md font-medium"
                                title="ContactForPrice (Ask For Price) inquiries"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                <span className="text-slate-500">Price:</span>
                                <span className="font-bold text-amber-900">{prod.contact_for_price_submissions || 0}</span>
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination.total_pages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
                <span className="text-xs text-slate-500">
                  Showing {(page - 1) * perPage + 1} to{" "}
                  {Math.min(page * perPage, pagination.total)} of {pagination.total} visited products
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    disabled={page <= 1 || loading}
                    className="p-1.5 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 transition-all"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <span className="px-3 py-1 text-xs font-semibold text-slate-700">
                    Page {page} of {pagination.total_pages}
                  </span>

                  <button
                    onClick={() => handlePageChange(Math.min(pagination.total_pages, page + 1))}
                    disabled={page >= pagination.total_pages || loading}
                    className="p-1.5 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 transition-all"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB 2: Contact Form Submissions ── */}
        {activeTab === "contacts" && (
          <div className="p-5 space-y-4">
            {/* Filter Buttons & Sub-search */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500">Form Source:</span>
                <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
                  <button
                    onClick={() => setContactFilterType("all")}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                      contactFilterType === "all"
                        ? "bg-white text-slate-900 shadow-sm font-bold"
                        : "text-slate-600"
                    }`}
                  >
                    All ({contactStats.total_submissions || 0})
                  </button>
                  <button
                    onClick={() => setContactFilterType("CardPopup")}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                      contactFilterType === "CardPopup"
                        ? "bg-cyan-600 text-white shadow-sm font-bold"
                        : "text-slate-600"
                    }`}
                  >
                    CardPopup ({contactStats.card_popup_submissions || 0})
                  </button>
                  <button
                    onClick={() => setContactFilterType("ContactForPrice")}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                      contactFilterType === "ContactForPrice"
                        ? "bg-amber-600 text-white shadow-sm font-bold"
                        : "text-slate-600"
                    }`}
                  >
                    ContactForPrice ({contactStats.contact_for_price_submissions || 0})
                  </button>
                  <button
                    onClick={() => setContactFilterType("General Contact")}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                      contactFilterType === "General Contact"
                        ? "bg-slate-700 text-white shadow-sm font-bold"
                        : "text-slate-600"
                    }`}
                  >
                    General ({contactStats.general_submissions || 0})
                  </button>
                </div>
              </div>

              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter inquiries by name, phone, product…"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            {/* Submissions Table */}
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-xs sm:text-sm">
                <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="py-3 px-4">#</th>
                    <th className="py-3 px-4">Inquirer Details</th>
                    <th className="py-3 px-4">Form Type</th>
                    <th className="py-3 px-4">Product / Inquired Item</th>
                    <th className="py-3 px-4">Date & Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="py-12 text-center text-slate-400">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
                        Loading form submissions…
                      </td>
                    </tr>
                  ) : !filteredContacts.length ? (
                    <tr>
                      <td colSpan="5" className="py-12 text-center text-slate-400">
                        No contact submissions found for the selected filter.
                      </td>
                    </tr>
                  ) : (
                    filteredContacts.map((c, idx) => (
                      <tr key={c.id || idx} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4 font-mono text-xs text-slate-400">{idx + 1}</td>
                        <td className="py-3 px-4">
                          <p className="font-semibold text-slate-900">{c.name}</p>
                          <div className="text-xs text-slate-500 space-y-0.5">
                            {c.contact_no && c.contact_no !== "—" && (
                              <p className="font-mono text-slate-700">{c.contact_no}</p>
                            )}
                            {c.email && c.email !== "—" && (
                              <p className="text-slate-500 text-[11px]">{c.email}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {c.form_type === "ContactForPrice" ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 border border-amber-300 rounded-lg text-xs font-bold">
                              <Tag className="w-3 h-3" />
                              ContactForPrice (Ask For Price)
                            </span>
                          ) : c.form_type === "CardPopup" ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-cyan-100 text-cyan-800 border border-cyan-300 rounded-lg text-xs font-bold">
                              <Layers className="w-3 h-3" />
                              CardPopup (Catalog / Product)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-semibold">
                              General Contact
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 max-w-sm">
                          <p className="font-medium text-slate-900 truncate">
                            {c.product_name || "General Inquiry"}
                          </p>
                          {c.product_url && (
                            <a
                              href={c.product_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] text-blue-600 hover:underline flex items-center gap-1 mt-0.5 truncate"
                            >
                              <span>{c.product_url}</span>
                              <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                            </a>
                          )}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">
                          {formatDate(c.created_date)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── TAB 3: Traffic & User Breakdown ── */}
        {activeTab === "traffic" && (
          <div className="p-5 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <span className="text-xs font-semibold text-slate-500 uppercase">
                  Logged In Members
                </span>
                <div className="text-2xl font-black text-slate-900 mt-1">
                  {traffic.logged_in_users || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Authenticated users</p>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <span className="text-xs font-semibold text-slate-500 uppercase">
                  Guest Visitors
                </span>
                <div className="text-2xl font-black text-slate-900 mt-1">
                  {traffic.guest_users || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Anonymous guest sessions</p>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <span className="text-xs font-semibold text-slate-500 uppercase">
                  Unique IP Addresses
                </span>
                <div className="text-2xl font-black text-slate-900 mt-1">
                  {traffic.unique_ips || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Geographic unique IPs</p>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <span className="text-xs font-semibold text-slate-500 uppercase">
                  Total Tracked Events
                </span>
                <div className="text-2xl font-black text-slate-900 mt-1">
                  {(traffic.total_events || 0).toLocaleString("en-IN")}
                </div>
                <p className="text-xs text-slate-500 mt-1">Clicks, views, actions</p>
              </div>
            </div>

            {/* Devices & Browsers breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Devices */}
              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-blue-600" /> Device Distribution
                </h3>
                <div className="space-y-2">
                  {(traffic.device_breakdown || []).map((dev) => (
                    <div
                      key={dev.device}
                      className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-xs"
                    >
                      <span className="font-semibold text-slate-700">{dev.device || "Other"}</span>
                      <span className="px-2 py-0.5 bg-white border border-slate-200 rounded-md font-bold text-blue-700">
                        {dev.count} visits
                      </span>
                    </div>
                  ))}
                  {!traffic.device_breakdown?.length && (
                    <p className="text-xs text-slate-400 py-3 text-center">
                      No device distribution recorded yet.
                    </p>
                  )}
                </div>
              </div>

              {/* Browsers */}
              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-indigo-600" /> Browser Distribution
                </h3>
                <div className="space-y-2">
                  {(traffic.browser_breakdown || []).map((b) => (
                    <div
                      key={b.browser}
                      className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-xs"
                    >
                      <span className="font-semibold text-slate-700">{b.browser || "Other"}</span>
                      <span className="px-2 py-0.5 bg-white border border-slate-200 rounded-md font-bold text-indigo-700">
                        {b.count} visits
                      </span>
                    </div>
                  ))}
                  {!traffic.browser_breakdown?.length && (
                    <p className="text-xs text-slate-400 py-3 text-center">
                      No browser distribution recorded yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductEventCount;